import $apiJSON from '../http/apiJSON'

// Anime API Service

export async function getAnimes(params = {}) {
  try {
    // Преобразуем параметры для соответствия API
    const apiParams = {}
    
    if (params.page) apiParams.page = params.page
    if (params.search) apiParams.search = params.search
    if (params.genre) apiParams.genre = params.genre
    if (params.year) apiParams.year = params.year
    if (params.rating) apiParams.min_rating = params.rating
    if (params.studio) apiParams.studio = params.studio
    if (params.type) apiParams.type = params.type
    if (params.status) apiParams.status = params.status
    if (params.ordering) apiParams.ordering = params.ordering

    const response = await $apiJSON.get('anime/anime/', { params: apiParams })
    
    return {
      results: response.data.results || response.data,
      total_pages: response.data.total_pages || Math.ceil((response.data.count || 0) / 20),
      count: response.data.count || 0
    }
  } catch (error) {
    console.error('Error fetching anime:', error)
    throw error
  }
}

export async function getAnime(slug) {
  return $apiJSON.get(`anime/anime/${slug}/`)
}

export async function getAnimeGenres() {
  return $apiJSON.get('anime/genres/')
}

export async function getGenres() {
  try {
    const response = await getAnimeGenres()
    return response.data.results || response.data
  } catch (error) {
    console.error('Error fetching anime genres:', error)
    return []
  }
}

export async function getAnimeStudios(params = {}) {
  return $apiJSON.get('anime/studios/', { params })
}

export async function getAnimeCharacters(params = {}) {
  return $apiJSON.get('anime/characters/', { params })
}

export async function rateAnime(slug, rating) {
  return $apiJSON.post(`anime/anime/${slug}/rate/`, { rating })
}

export async function getAnimeRating(slug) {
  return $apiJSON.get(`anime/anime/${slug}/rating/`)
}

export async function updateWatchStatus(slug, status) {
  return $apiJSON.post(`anime/anime/${slug}/status/`, { status })
}

export async function getAnimeWatchlist() {
  return $apiJSON.get('anime/watchlist/')
}

export async function getWatchlist() {
  try {
    const response = await getAnimeWatchlist()
    return response.data.results || response.data
  } catch (error) {
    console.error('Error fetching anime watchlist:', error)
    return []
  }
}

export async function addToWatchlist(animeId, status = 'watching') {
  return updateWatchStatus(animeId, status)
}

export async function removeFromWatchlist(animeId) {
  // Anime watchlist не удаляется, а обновляется статус на 'dropped'
  return updateWatchStatus(animeId, 'dropped')
}

export async function getAnimeReviews(slug, params = {}) {
  try {
    const response = await $apiJSON.get(`anime/anime/${slug}/reviews/`, { params })
    return {
      results: response.data.results || response.data,
      total_pages: response.data.total_pages || 1
    }
  } catch (error) {
    console.error('Error fetching anime reviews:', error)
    return { results: [], total_pages: 1 }
  }
}

export async function addAnimeReview(slug, content, rating) {
  return $apiJSON.post(`anime/anime/${slug}/add-review/`, { content, rating })
}

export async function submitReview(animeId, content, rating) {
  return addAnimeReview(animeId, content, rating)
}

export async function updateAnimeReview(reviewId, data) {
  return $apiJSON.put(`anime/reviews/${reviewId}/`, data)
}

export async function deleteAnimeReview(reviewId) {
  return $apiJSON.delete(`anime/reviews/${reviewId}/`)
}

export async function getAnimeByGenre(genreId, params = {}) {
  return getAnimes({ genre: genreId, ...params })
}

export async function getAnimeByStudio(studioId, params = {}) {
  return getAnimes({ studio: studioId, ...params })
}

export async function searchAnime(query, page = 1) {
  return getAnimes({ search: query, page })
}

export async function getPopularAnime(page = 1) {
  return getAnimes({ page, ordering: '-rating_avg' })
}

export async function getRecentAnime(page = 1) {
  return getAnimes({ page, ordering: '-created_at' })
}

export async function getSeasonalAnime(year, season, page = 1) {
  return getAnimes({ 
    page,
    year: year, 
    season: season 
  })
}

export async function getAnimeByType(type, page = 1) {
  return getAnimes({ page, type })
}

export async function getAnimeByStatus(status, page = 1) {
  return getAnimes({ page, status })
}

export async function getAnimeEpisodes(slug, params = {}) {
  return $apiJSON.get(`anime/anime/${slug}/episodes/`, { params })
}

export async function markEpisodeWatched(episodeId, progress = 100) {
  return $apiJSON.post(`anime/episodes/${episodeId}/mark-watched/`, { 
    progress_percent: progress 
  })
}

export async function likeAnime(slug) {
  return $apiJSON.post(`anime/anime/${slug}/like/`)
}

export async function getAnimeStats() {
  return $apiJSON.get('anime/stats/')
}

export async function getFavorites() {
  // В anime-сервисе лайки служат как избранное
  try {
    const response = await $apiJSON.get('anime/history/')
    return response.data.results || response.data
  } catch (error) {
    console.error('Error fetching anime favorites:', error)
    return []
  }
}

export async function addToFavorites(animeId) {
  return likeAnime(animeId)
}

export async function removeFromFavorites(animeId) {
  return likeAnime(animeId) // Toggle function
}

// Класс-обертка для удобства использования
class AnimeService {
  static getAnimes = getAnimes
  static getAnime = getAnime
  static getGenres = getGenres
  static getAnimeStudios = getAnimeStudios
  static getAnimeCharacters = getAnimeCharacters
  static rateAnime = rateAnime
  static getAnimeRating = getAnimeRating
  static updateWatchStatus = updateWatchStatus
  static getWatchlist = getWatchlist
  static addToWatchlist = addToWatchlist
  static removeFromWatchlist = removeFromWatchlist
  static getAnimeReviews = getAnimeReviews
  static submitReview = submitReview
  static searchAnime = searchAnime
  static getPopularAnime = getPopularAnime
  static getRecentAnime = getRecentAnime
  static getSeasonalAnime = getSeasonalAnime
  static getAnimeByType = getAnimeByType
  static getAnimeByStatus = getAnimeByStatus
  static getAnimeEpisodes = getAnimeEpisodes
  static markEpisodeWatched = markEpisodeWatched
  static likeAnime = likeAnime
  static getAnimeStats = getAnimeStats
  static getFavorites = getFavorites
  static addToFavorites = addToFavorites
  static removeFromFavorites = removeFromFavorites
  
  // Legacy methods
  static getMovies = getAnimes
  static getMovieDetails = getAnime
}

export default AnimeService